import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { type CustomerRow, formatPhone, type OrganizationRow, ORG_TYPE_LABEL, type PartyRef } from '@reikraft/shared';
import { Field, Segmented } from '@reikraft/ui';
import { api, qs } from '../api';
import { useDebounced } from '../hooks';

export type PartyValue =
  | { kind: 'customer'; id: string; name: string }
  | { kind: 'organization'; id: string; name: string }
  | { kind: 'new'; name: string; phone: string; avitoUrl: string };

/** Выбор клиента: найденный частный клиент, организация или новый клиент */
export function PartyPicker({
  value,
  onChange,
  allowNew = true,
  errors = {},
}: {
  value: PartyValue | null;
  onChange: (v: PartyValue | null) => void;
  allowNew?: boolean;
  errors?: Record<string, string>;
}) {
  const [mode, setMode] = useState<'customer' | 'organization'>(value?.kind === 'organization' ? 'organization' : 'customer');
  const [q, setQ] = useState('');
  const dq = useDebounced(q);
  const customers = useQuery({
    queryKey: ['customers', dq],
    queryFn: () => api.get<CustomerRow[]>(`/admin/customers${qs({ q: dq })}`),
    enabled: mode === 'customer' && dq.length >= 2,
  });
  const orgs = useQuery({
    queryKey: ['organizations', dq],
    queryFn: () => api.get<OrganizationRow[]>(`/admin/organizations${qs({ q: dq })}`),
    enabled: mode === 'organization',
  });

  if (value && value.kind !== 'new') {
    return (
      <div className="rk-row" style={{ justifyContent: 'space-between', padding: '10px 12px', border: '1px solid var(--line-2)' }}>
        <span>
          <b>{value.name}</b> <span className="rk-muted">· {value.kind === 'organization' ? 'организация' : 'частный клиент'}</span>
        </span>
        <button type="button" className="rk-btn rk-btn--sm" onClick={() => onChange(null)}>
          Другой клиент
        </button>
      </div>
    );
  }

  const isNew = value?.kind === 'new';
  return (
    <div className="rk-stack" style={{ ['--rk-gap' as string]: '10px' }}>
      <Segmented
        label="Тип клиента"
        value={mode}
        onChange={(m) => {
          setMode(m);
          setQ('');
          onChange(null);
        }}
        options={[
          { value: 'customer', label: 'Частное лицо' },
          { value: 'organization', label: 'Организация' },
        ]}
      />
      {!isNew && (
        <Field
          label={mode === 'customer' ? 'Найти клиента по имени или телефону' : 'Найти организацию'}
          error={errors.customerId ?? errors.organizationId}
        >
          <input
            className="rk-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={mode === 'customer' ? 'Например, Николаев или 4512' : 'Название или ИНН'}
          />
        </Field>
      )}
      {!isNew && mode === 'customer' && dq.length >= 2 && (
        <div className="adm-cards" style={{ maxHeight: 240, overflowY: 'auto' }}>
          {(customers.data ?? []).map((c) => (
            <button
              key={c.id}
              type="button"
              className="adm-card"
              style={{ background: 'transparent', border: 0, borderBottom: '1px solid var(--line)', textAlign: 'left' }}
              onClick={() => onChange({ kind: 'customer', id: c.id, name: c.name })}
            >
              <span className="adm-card__top">
                <b>{c.name}</b>
                <span className="rk-mono rk-muted">{formatPhone(c.phone)}</span>
              </span>
              <span className="adm-card__sub">заказов: {c.ordersCount}</span>
            </button>
          ))}
          {customers.data && !customers.data.length && (
            <div className="rk-empty" style={{ padding: 16 }}>
              Не нашли — заведите нового клиента ниже
            </div>
          )}
        </div>
      )}
      {mode === 'organization' && (
        <div className="adm-cards" style={{ maxHeight: 240, overflowY: 'auto' }}>
          {(orgs.data ?? []).map((g) => (
            <button
              key={g.id}
              type="button"
              className="adm-card"
              style={{ background: 'transparent', border: 0, borderBottom: '1px solid var(--line)', textAlign: 'left' }}
              onClick={() => onChange({ kind: 'organization', id: g.id, name: g.name })}
            >
              <span className="adm-card__top">
                <b>{g.name}</b>
                <span className="rk-muted">{ORG_TYPE_LABEL[g.type]}</span>
              </span>
              <span className="adm-card__sub">
                {g.contactName ?? '—'} · активных заказов: {g.activeOrders}
              </span>
            </button>
          ))}
          {orgs.data && !orgs.data.length && (
            <div className="rk-empty" style={{ padding: 16 }}>
              Организаций нет — добавьте в разделе «Организации»
            </div>
          )}
        </div>
      )}
      {mode === 'customer' && allowNew && (
        <>
          {!isNew ? (
            <button
              type="button"
              className="rk-btn rk-btn--sm"
              style={{ alignSelf: 'flex-start' }}
              onClick={() => onChange({ kind: 'new', name: q && !/\d{3}/.test(q) ? q : '', phone: /\d{3}/.test(q) ? q : '', avitoUrl: '' })}
            >
              + Новый клиент
            </button>
          ) : (
            <div className="rk-grid-form">
              <Field label="Имя клиента" error={errors['newCustomer.name']}>
                <input className="rk-input" value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} autoFocus />
              </Field>
              <Field label="Телефон" error={errors['newCustomer.phone']}>
                <input
                  className="rk-input rk-input--mono"
                  type="tel"
                  value={value.phone}
                  onChange={(e) => onChange({ ...value, phone: e.target.value })}
                  placeholder="+7 (___) ___-__-__"
                />
              </Field>
              <Field label="Профиль на Авито" hint="Необязательно">
                <input
                  className="rk-input"
                  value={value.avitoUrl}
                  onChange={(e) => onChange({ ...value, avitoUrl: e.target.value })}
                  placeholder="https://www.avito.ru/…"
                />
              </Field>
              <button type="button" className="rk-btn rk-btn--sm rk-btn--link" style={{ alignSelf: 'end' }} onClick={() => onChange(null)}>
                Найти существующего
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function partyToBody(v: PartyValue | null) {
  if (!v) return {};
  if (v.kind === 'customer') return { customerId: v.id };
  if (v.kind === 'organization') return { organizationId: v.id };
  return { newCustomer: { name: v.name, phone: v.phone || null, avitoUrl: v.avitoUrl || null } };
}

export function partyFromRef(p: PartyRef | null): PartyValue | null {
  if (!p) return null;
  return p.kind === 'organization' ? { kind: 'organization', id: p.id, name: p.name } : { kind: 'customer', id: p.id, name: p.name };
}
